import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import { routes } from "pages/routes";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { ApiRequestError } from "services/errors";
import {
  OAuthProvider,
  OAuthStep,
  confirmOAuth,
  ConfirmOAuthResponse,
  OAuthUserInfo,
  startOAuth,
  verifyEmail,
} from "services/oauth";
import { SignInLocationState } from "pages/SignIn/SignIn";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams, Location } from "react-router";
import { useSearchParams } from "react-router-dom";
import { logError } from "services/logging";
import { isEnumMember } from "utils/enums";
import { trackEvent, useTracking } from "services/productAnalytics";
import { PurchaseStudentType } from "services/pricingModel/model";
import { dynamicKey } from "services/i18n";

/**
 * Custom hook that handles user authentication and navigation after sign-in.
 */
function useSignin() {
  const location: Location<SignInLocationState> = useLocation();
  const { refreshCurrentUser } = useRequiredContext(UserContext);

  const signIn = useCallback(
    async (returnUrl?: string) => {
      await refreshCurrentUser();
      trackEvent("sign_in_success");
      const redirectUrl = returnUrl ?? routes.root.path() + (location.hash || "");

      window.location.replace(redirectUrl);
    },
    [location.hash, refreshCurrentUser]
  );

  return signIn;
}

interface Props {
  provider: OAuthProvider;
  step: OAuthStep;
}

function ValidatedOAuthContent({ provider, step }: Props) {
  const navigate = useNavigate();
  const location: Location<SignInLocationState> = useLocation();
  const { t } = useTranslation();

  const signIn = useSignin();
  const [searchParams] = useSearchParams();

  const [error, setError] = useState<string | null>(null);
  const [ssoRedirectEmail, setSsoRedirectEmail] = useState<string | null>(null);

  const pricingModelCode = searchParams.get("code");
  const studentType = searchParams.get("studentType") as PurchaseStudentType;
  const returnPath = location.state?.returnPath;

  useTracking(
    {
      name: "social_sign_in_step",
      step,
    },
    [step]
  );

  useEffect(() => {
    const action = async () => {
      let userInfo: OAuthUserInfo | undefined = undefined;

      const onUserConfirmed = (confirmationResults: ConfirmOAuthResponse) => {
        if (confirmationResults.verify_email) {
          setError("Auth.verifyEmail.clickVerificationLink");
          return;
        }
        userInfo = confirmationResults.user;
      };

      try {
        const oauthParams = Object.fromEntries(searchParams.entries());
        switch (step) {
          case OAuthStep.Start: {
            const response = await startOAuth(provider, {
              pricing_model_code: pricingModelCode,
              student_type: studentType ?? "self-study",
              return_path: returnPath,
            });
            window.location.href = response.url; // Redirect to the OAuth provider
            return;
          }
          case OAuthStep.PostConfirm: {
            if (!oauthParams.response) {
              setError("Auth.callback.invalidResponse");
              return;
            }
            const response = JSON.parse(oauthParams.response);
            if (response.error) {
              if (response.error_code === "sso_required") {
                setSsoRedirectEmail((response.email as string) ?? "");
                return;
              }
              setError(
                response.error_code
                  ? `Auth.errorCode.${response.error_code}`
                  : "Auth.callback.genericError"
              );
            } else {
              onUserConfirmed(response as ConfirmOAuthResponse);
            }
            break;
          }
          case OAuthStep.Confirm: {
            const confirmationResults = await confirmOAuth(provider, oauthParams);
            onUserConfirmed(confirmationResults);
            break;
          }
          case OAuthStep.VerifyEmail: {
            if (!oauthParams?.token) {
              setError("Auth.verifyEmail.invalidToken");
              return;
            }
            userInfo = await verifyEmail(oauthParams.token);
            break;
          }
        }

        if (!userInfo) {
          throw new Error("No user info received from OAuth provider"); // Should never happen
        }

        if (userInfo.mfa) {
          const locationState: SignInLocationState = {
            mfaToken: userInfo.token,
            email: userInfo.email,
            returnPath: returnPath,
          };
          navigate(routes.public.signIn.path(), {
            state: locationState,
          });
          return;
        } else {
          await signIn(userInfo.return_path);
        }
      } catch (e) {
        logError("An error during OAuth", e);
        const firstError = e instanceof ApiRequestError ? e.errors?.[0] : undefined;
        trackEvent("sign_in_error", {
          status: e instanceof ApiRequestError ? e.response?.status : undefined,
          error_code: firstError?.error_code,
        });
        if (firstError?.error_code === "sso_required") {
          // SSO-managed account: social sign-in can't complete their activation.
          setSsoRedirectEmail((firstError.email as string) ?? "");
          return;
        }
        let message = "Auth.callback.genericError";
        if (firstError?.error_code) {
          message = `Auth.errorCode.${firstError.error_code}`;
        }
        setError(message);
      }
    };
    action();
  }, [
    navigate,
    provider,
    signIn,
    step,
    searchParams,
    pricingModelCode,
    returnPath,
    studentType,
  ]);

  if (ssoRedirectEmail !== null) {
    const query = new URLSearchParams(
      ssoRedirectEmail ? { email: ssoRedirectEmail } : {}
    );
    return <Navigate to={`${routes.public.signInSso.path()}?${query}`} replace />;
  }

  if (error) {
    return (
      <Navigate
        to={routes.public.signIn.path()}
        replace
        state={{ error: t(dynamicKey(error)) }}
      />
    );
  }

  return <TalaeraSpinner />;
}

export default function OAuthLoadingPage() {
  const { provider, step } = useParams();

  // FIXME: Rework this when (if) [zod] is added to the project.
  if (
    !provider ||
    !isEnumMember(OAuthProvider, provider) ||
    !step ||
    !isEnumMember(OAuthStep, step)
  ) {
    return <Navigate to={routes.public.notFound.path()} replace />;
  }

  return (
    <ValidatedOAuthContent
      provider={provider as OAuthProvider}
      step={step as OAuthStep}
    />
  );
}
